import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import { useBlogContext } from '../../context/BlogContext';
import { supabaseClient } from '../../config/supabase-client';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ManagePage.css';

const ManageEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { blogs, draftBlogs, updateBlog, addBlog } = useBlogContext();

    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [summary, setSummary] = useState('');
    const [content, setContent] = useState('');
    const [isDraft, setIsDraft] = useState(true);
    const [blogId, setBlogId] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');

    // Login gate (same as ManagePage)
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState<string | null>(null);
    const [loggingIn, setLoggingIn] = useState(false);

    useEffect(() => {
        if (id && id !== 'new') {
            const found = [...blogs, ...draftBlogs].find(b => b.id === id);
            if (found) {
                setTitle(found.title);
                setAuthor(found.author || '');
                setSummary(found.summary || '');
                setContent(found.content);
                setIsDraft(found.is_draft);
                setBlogId(found.id);
            }
        }
    }, [id, blogs, draftBlogs]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoggingIn(true);
        setLoginError(null);
        const { error } = await supabaseClient.auth.signInWithPassword({ email, password });
        if (error) setLoginError(error.message);
        setLoggingIn(false);
    };

    const handleSave = () => {
        const blog = {
            id: blogId || uuidv4(),
            title,
            content,
            author,
            summary,
            is_draft: isDraft,
            published_at: isDraft ? null : new Date().toISOString(),
        };

        if (blogId) {
            updateBlog(blog);
        } else {
            addBlog(blog);
            setBlogId(blog.id);
            navigate(`/manage/edit/${blog.id}`, { replace: true });
        }

        setSaveMsg('Saved.');
        setTimeout(() => setSaveMsg(''), 2000);
    };

    const handlePublish = () => {
        const newDraft = !isDraft;
        setIsDraft(newDraft);

        const blog = {
            id: blogId || uuidv4(),
            title,
            content,
            author,
            summary,
            is_draft: newDraft,
            published_at: newDraft ? null : new Date().toISOString(),
        };

        if (blogId) {
            updateBlog(blog);
        } else {
            addBlog(blog);
            setBlogId(blog.id);
            navigate(`/manage/edit/${blog.id}`, { replace: true });
        }

        setSaveMsg(newDraft ? 'Unpublished.' : 'Published.');
        setTimeout(() => setSaveMsg(''), 2000);
    };

    if (!user) {
        return (
            <div className="manage-page">
                <div className="manage-login">
                    <h1>Manage</h1>
                    <form onSubmit={handleLogin}>
                        <label>
                            Email
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" required />
                        </label>
                        <label>
                            Passcode
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                        </label>
                        {loginError && <p className="manage-error">{loginError}</p>}
                        <button type="submit" disabled={loggingIn}>
                            {loggingIn ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-page">
            <div className="manage-editor">
                <button className="manage-back" onClick={() => navigate('/manage')}>← back to manage</button>
                <h1>{blogId ? 'Edit Post' : 'New Post'}</h1>

                <div className="manage-editor-fields">
                    <label>
                        Title
                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Post title" />
                    </label>
                    <label>
                        Author
                        <input type="text" value={author} onChange={e => setAuthor(e.target.value)} placeholder="Your name" />
                    </label>
                    <label>
                        Summary
                        <input type="text" value={summary} onChange={e => setSummary(e.target.value)} placeholder="One-line summary (shown on list page)" />
                    </label>
                </div>

                <div className="manage-editor-tabs">
                    <button
                        className={`manage-editor-tab ${!showPreview ? 'active' : ''}`}
                        onClick={() => setShowPreview(false)}
                    >
                        Write
                    </button>
                    <button
                        className={`manage-editor-tab ${showPreview ? 'active' : ''}`}
                        onClick={() => setShowPreview(true)}
                    >
                        Preview
                    </button>
                </div>

                {showPreview ? (
                    <div className="manage-editor-preview">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                ) : (
                    <textarea
                        className="manage-editor-textarea"
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        placeholder="Write in Markdown…"
                    />
                )}

                <div className="manage-editor-actions">
                    <button onClick={handleSave}>Save</button>
                    <button className="btn-publish" onClick={handlePublish}>
                        {isDraft ? 'Publish' : 'Unpublish'}
                    </button>
                </div>
                {saveMsg && <p className="manage-save-msg">{saveMsg}</p>}
            </div>
        </div>
    );
};

export default ManageEditor;
